import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ContentTypes, DialogTypes } from "../constants/action-types";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import { useQuery } from "../AppLayout";
import CalendarView from "../calendar/CalendarView";
import { useCurrentSize } from "../CurrentSize";
import BulkEditForm from "../board/components/TaskDetail/BulkEditForm";
import { SmallLoading } from "../main/Loading";
import TaskAction from "../actions/TaskAction";
import TrackingPanel from "../chrome/side-panel/TrackingPanel";

const RightPanelContainer = styled.div`
  position: fixed;
  right: 0;
  height: 100%;
  top: 0;
  background-color: rgb(255, 255, 255);
  box-shadow:
    rgb(0 0 0 / 22%) 0px 25.6px 57.6px 0px,
    rgb(0 0 0 / 18%) 0px 4.8px 14.4px 0px;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  inset: 0px 0px 0px auto;
  animation: 0s ease 0s 1 normal none running none;
  z-index: 30;
`;

const RightPanelDialog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const show = useSelector((state: any) =>
    state.taskReducer.getIn(["showRightPanelDialog", "show"])
  );
  const currentSize = useCurrentSize();
  const { projectId }: any = useParams();
  const contentType = useQuery().get("type");
  const dialogType = useSelector((state: any) =>
    state.taskReducer.getIn(["showRightPanelDialog", "type"])
  );
  const messageId = useSelector((state: any) =>
    state.taskReducer.getIn(["showRightPanelDialog", "data", "messageId"])
  );

  const isSideDialog = () => {
    return (
      dialogType == DialogTypes.sideMyInboxDialog ||
      dialogType == DialogTypes.sideDialog ||
      dialogType == DialogTypes.sideMyInboxMessageDialog ||
      dialogType == DialogTypes.sideBulkEditDialog
    );
  };

  const display = show && isSideDialog() ? "block" : "none";

  const getWidth = () => {
    if (dialogType == DialogTypes.sideMyInboxMessageDialog) {
      return 400;
    }
    return 600;
  };

  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const sortFields = useSelector((state: any) => state.taskReducer.getIn(["sorts"]));

  const onClose = () => {
    dispatch(TaskAction.hideRightPanelDialog(DialogTypes.sideDialog));
  };

  const handleSearchTasks = ({
    projectId,
    page = 0,
    pageSize = 500,
    filters = taskFilters.toJS(),
    types = [ContentTypes.ACTIVITY],
    sorts = sortFields.toJS(),
  }: any) => {
    if (page >= 0 && projectId) {
      const searchRequest = {
        projectId,
        page,
        pageSize,
        filters,
        view: "CALENDAR",
        types,
        sorts,
      };
      return dispatch(TaskAction.searchItems(searchRequest));
    }
    return Promise.resolve();
  };

  const renderHeader = () => {
    if (dialogType == DialogTypes.sideMyInboxDialog) {
      return (
        <div className={"w-full flex space-x-2 p-4 pb-0 justify-between items-center"}>
          <div className={"w-24 text-lg"}>My work</div>
          <XMarkIcon className={"w-6 justify-end cursor-pointer"} onClick={onClose} />
        </div>
      );
    }
    if (dialogType == DialogTypes.sideDialog) {
      return (
        <div className={"w-full flex"}>
          <div className={"w-full text-lg"}>Create</div>
          <XMarkIcon className={"w-6 justify-end cursor-pointer"} onClick={onClose} />
        </div>
      );
    }
    return null;
  };
  const renderContent = () => {
    if (dialogType == DialogTypes.sideBulkEditDialog) {
      return <BulkEditForm onClose={onClose} contentType={contentType} projectId={projectId} />;
    }
    if (dialogType == DialogTypes.sideMyInboxDialog) {
      return (
        <div className={"w-full h-full"}>
          <CalendarView
            projectId={projectId}
            currentSize={currentSize}
            onSearchTasks={handleSearchTasks}
            initialView="timeGridDay"
            allowedViews={["timeGridDay"]}
            hideViewTabs
            calendarHref={`/workspaces/${projectId}?view=CALENDAR&type=ACTIVITY&key=Activities`}
            onCalendarClick={() => {
              setTimeout(() => {
                onClose();
              }, 500);
            }}
            fullHeight
            height={"90%"}
          />
        </div>
      );
    }
    if (dialogType == DialogTypes.sideMyInboxMessageDialog) {
      return (
        <div className="h-full">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <div>
              <h2 className="text-lg text-gray-800 font-semibold">{t("Tracking")}</h2>
            </div>
            <button
              onClick={() => {
                onClose();
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-6" />
            </button>
          </div>
          <TrackingPanel messageId={messageId} />
        </div>
      );
    }
    return <SmallLoading />;
  };
  return (
    <RightPanelContainer
      style={{
        display,
        width: getWidth(),
      }}
    >
      {renderHeader()}
      {renderContent()}
    </RightPanelContainer>
  );
};

export default RightPanelDialog;
